import { Controller } from "@hotwired/stimulus"

// Mobile-only slide-up video popover (docs/features/v1/video-player.md, Phase 4).
//
// Desktop is unchanged: the search-grid card link is a normal full-page nav to
// the watch page (copying YouTube web). Below the `md` breakpoint a tap opens a
// bottom sheet instead. On tap we start the slide-up animation AND fetch the
// watch page concurrently, so the YouTube iframe loads under the motion and the
// open feels instant. The URL is synced via pushState so back/forward and shared
// deep-links behave (a cold deep-link just loads the full watch page).
//
// Content comes from the server-rendered watch page (single source of truth for
// the player + the Phase 3 resume handoff); we extract its `#video_player` node.
export default class extends Controller {
  static targets = ["sheet", "panel", "content"]
  static values = { open: { type: Boolean, default: false } }

  connect() {
    this.boundClick = this.handleClick.bind(this)
    this.boundPopState = this.handlePopState.bind(this)
    this.element.addEventListener("click", this.boundClick)
    window.addEventListener("popstate", this.boundPopState)
  }

  disconnect() {
    this.element.removeEventListener("click", this.boundClick)
    window.removeEventListener("popstate", this.boundPopState)
    document.body.style.overflow = ""
  }

  // Delegated: only intercept a primary click on a watch link, on mobile, for an
  // embeddable video. Anything else falls through to normal navigation.
  handleClick(event) {
    if (event.defaultPrevented) return
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest('a[href*="/watch/"]')
    if (!link) return
    if (!this.isMobile()) return // desktop: let the link navigate

    const card = link.closest(".search-result")
    if (card && card.dataset.embeddable === "false") return // not embeddable → link out

    event.preventDefault()
    // The card's thumbnail is already loaded/cached; reuse it as an instant
    // poster so the sheet isn't black while the iframe loads.
    const thumb = card && card.querySelector(".search-result__thumbnail-image")
    this.openPopover(link.href, thumb ? thumb.currentSrc || thumb.src : null)
  }

  isMobile() {
    return window.matchMedia("(max-width: 767px)").matches
  }

  // Start the slide and the fetch at the same moment (the motion masks the load).
  openPopover(url, thumbnailSrc = null) {
    this.thumbnailSrc = thumbnailSrc
    this.showSheet()
    this.showThumbnailPlaceholder()
    this.pushHistoryEntry()
    this.loadPlayer(url)
  }

  // Show the cached thumbnail immediately (before the fetch returns) so the
  // sliding sheet reveals the poster, not a black box.
  showThumbnailPlaceholder() {
    if (!this.thumbnailSrc) return
    const wrap = document.createElement("div")
    wrap.className = "relative w-full aspect-[16/9] bg-black"
    wrap.appendChild(this.buildThumbnail())
    this.contentTarget.replaceChildren(wrap)
  }

  buildThumbnail() {
    const img = document.createElement("img")
    img.src = this.thumbnailSrc
    img.className = "absolute inset-0 w-full h-full object-cover"
    img.setAttribute("aria-hidden", "true")
    return img
  }

  showSheet() {
    document.body.style.overflow = "hidden"
    // Reveal at the start state: panel fully below the screen.
    this.sheetTarget.classList.remove("hidden")
    // Force a synchronous reflow so the browser PAINTS that start state before we
    // change it — otherwise the class removal below collapses into the same frame
    // and the panel appears already part-way up instead of rising from the bottom.
    void this.panelTarget.offsetHeight
    // Slide the (opaque) panel up to cover the screen. No backdrop fade — the
    // slide itself covers the page.
    this.panelTarget.classList.remove("translate-y-full")
    this.openValue = true
  }

  hideSheet() {
    this.panelTarget.classList.add("translate-y-full")
    document.body.style.overflow = ""
    this.openValue = false
    // After the slide-down finishes, hide the layer and clear the player so the
    // iframe stops and audio dies. Must match the panel's transition duration.
    setTimeout(() => {
      this.sheetTarget.classList.add("hidden")
      this.contentTarget.replaceChildren()
    }, 500)
  }

  async loadPlayer(url) {
    try {
      const response = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      if (!response.ok) return this.fallbackToNav(url)

      const doc = new DOMParser().parseFromString(await response.text(), "text/html")
      const player = doc.querySelector("#video_player")
      if (!player) return this.fallbackToNav(url)

      // The watch page's <body> carries the playbacks URL the player POSTs to;
      // the search page (application layout) doesn't, so copy it before injecting.
      const playbacksUrl = doc.body.dataset.playbacksUrl
      if (playbacksUrl && !document.body.dataset.playbacksUrl) {
        document.body.dataset.playbacksUrl = playbacksUrl
      }

      if (!this.openValue) return // closed during the fetch

      const playerNode = document.importNode(player, true)
      // Carry the thumbnail as an overlay inside the player (z-10, below the
      // resume overlay's z-20) so it keeps masking the black iframe-loading gap
      // after we swap the placeholder out; removed once the iframe loads.
      if (this.thumbnailSrc) {
        const poster = this.buildThumbnail()
        poster.classList.add("z-10", "pointer-events-none")
        playerNode.appendChild(poster)
        this.removePosterWhenReady(playerNode, poster)
      }
      // Importing + inserting connects the segment-player controller → iframe loads.
      this.contentTarget.replaceChildren(playerNode)
    } catch (error) {
      this.fallbackToNav(url)
    }
  }

  // Remove the poster once the YouTube iframe has loaded (small delay so YouTube
  // has painted), with a safety timeout in case the load event never fires.
  removePosterWhenReady(playerNode, poster) {
    let done = false
    const remove = () => {
      if (done) return
      done = true
      poster.remove()
    }
    const start = Date.now()
    const waitForIframe = () => {
      if (done) return
      const iframe = playerNode.querySelector("iframe")
      if (iframe) {
        iframe.addEventListener("load", () => setTimeout(remove, 200))
      } else if (Date.now() - start < 4000) {
        requestAnimationFrame(waitForIframe)
      }
    }
    waitForIframe()
    setTimeout(remove, 4000) // safety net
  }

  fallbackToNav(url) {
    window.location.href = url
  }

  // Push a history entry WITHOUT changing the URL, purely so the hardware back
  // button closes the popover. We deliberately do NOT pushState the watch URL:
  // Turbo asynchronously rewrites history around any manual URL change, which
  // races close() non-deterministically. The watch URL stays shareable on its
  // own — a cold visit to it opens the full page. (Track "did we push" on the
  // instance, not history.state, which Turbo also clobbers.)
  pushHistoryEntry() {
    history.pushState({ videoPopover: true }, "")
    this.pushed = true
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.openValue) return

    if (this.pushed) {
      // Pop our state entry; popstate → handlePopState closes the sheet. The URL
      // never changed, so there is nothing to race.
      this.pushed = false
      history.back()
    } else {
      this.hideSheet()
    }
  }

  backdropClick(event) {
    if (event.target === event.currentTarget) this.close(event)
  }

  // Hardware back/forward (or our own history.back) → close the sheet. We stay on
  // the search page (never unmounted), so its scroll position is preserved.
  handlePopState() {
    if (!this.openValue) return
    this.pushed = false
    this.hideSheet()
  }
}
